import Vue from 'vue';
import BaseRepository from './BaseRepository';

class SpaceRepository extends BaseRepository {

    /**
     * Reserve a space
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    reserve(data) {
        return Vue.http.post(`/api/v2/spaces/${data.id}/reserve`, data);
    }

    /**
     * Reserve multiple bookings for a space
     * TODO: Change to multi end point?
     * @param data
     */
    reserveMulti(data) {
        return Vue.http.post(`spaces/${data.id}/reserve`, data);
    }

    /**
     * Get a quote for a space
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    getQuote(data) {
        return Vue.http.post(`/api/v2/spaces/${data.id}/quote`, data);
    }

    /**
     * Get a service fee for a multi-day booking
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    getMultiDayServiceFee(data) {
        return Vue.http.post(`/api/v2/spaces/${data.id}/quote/service-fee`, data);
    }

    /**
     * Applies voucher code
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    applyVoucherCode(data) {
        return Vue.http.post(`spaces/${data.id}/quote/apply-discount-code`, data);
    }

    /**
     * Perform a search query
     *
     * @param {object} data - query data
     *
     * @returns {Promise}
     */
    query(data) {
        window.lastSearchParams = data;
        return Vue.http.get('search', {
            params: data
        });
    }

    /**
     * Purchase a space
     *
     * @param {object} data - booking data
     *
     * @returns {Promise}
     */
    purchase(data) {
        return Vue.http.post(`spaces/${data.id}/purchase`, data);
    }

    /**
     * Purchase a space, with no payment
     *
     * @param {object} data - booking data
     *
     * @returns {Promise}
     */
    skipPurchase(data) {
        return Vue.http.post(this.route('ajax.space.purchase-admin', { spaceId: data.id }), data);
    }

    /**
     * Submit a space enquiry
     *
     * @param {object} data - Enquiry data
     *
     * @returns {Promise}
     */
    submitEnquiry(data) {
        if (data.name) {
            let fullName = data.name.split(" ");
            data.first_name = fullName[0];
            data.last_name = fullName[fullName.length - 1];
        }
        return Vue.http.post(`spaces/${data.spaceId}/enquire`, data);
    }

    /**
     * Add user to the space waiting list
     *
     * @param {number} id - Space id
     * @param {object} data - Enquiry data
     *
     * @returns {Promise}
     */
    addToWaitingList(id, data = {}) {
        return Vue.http.post(`spaces/${id}/subscribe`, data);
    }

    /**
     * Register the user for search notifications
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    submitNotification(data) {
        return Vue.http.post('location-notification', data)
    }

    /**
     * Add user to the waiting list for a space
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    submitWaitingList(data) {
        return Vue.http.post(`spaces/${data.spaceId}/subscribe`, data)
    }

    /**
     * Fetch a specific space by ID
     *
     * @param {object} searchData - Search data
     * @param {object} [data={}] - Additional data
     *
     * @returns {Promise}
     */
    find(searchData, data = {}) {
        return Vue.http.get(this.route('api.v2.spaces.show', searchData), data);
    }

    /**
     * Fetch reviews for a space by id
     *
     * @param {number} id - Space ID
     * @param {object} [params={}] - Additional params
     *
     * @returns {Promise}
     */
    reviews(id, params = {}) {
        return Vue.http.get(`spaces/${id}/reviews`, {
            params,
        });
    }

    /**
     * Fetch faqs for a space by id. These are currently hardcoded here,
     * but the groundwork is set for the API to provide
     *
     * @param {number} id - Space ID
     * @param {object} [data={}] - Additional data
     *
     * @returns {Promise}
     */
    faqs(id, data = {}) {
        return new Promise((resolve) => {
            let faqs = [
                {
                    title: 'Can the space accommodate a particular size vehicle?',
                    content: 'The size of vehicles suitable are listed below the parking space description under the section “Suitable for”. Any height restrictions will be detailed in the description.'
                },
                {
                    type: 'short',
                    title: 'Is this space available for the dates I need?',
                    content: 'Parking spaces that appear as a result of your search are all available for instant booking. Spaces which are not available will not show in the results.'
                },
                {
                    type: 'short',
                    title: 'After I book a parking space, is it guaranteed?',
                    content: 'Yes of course! After you book confirmation will be in your account, we’ll also email instructions together with the owner’s contact details. When it’s time to park, drive to your spot, following the directions. Also remember you are protected by our Booking Guarantee.'
                },
                {
                    type: 'short',
                    title: 'Do I have to enter and leave exactly at the times listed?',
                    content: 'No way, you set the schedule, just don\'t park outside the booking window. For example, if your booking is from 7am - 7pm, you can show up late for work at 9am and cut out early at 4pm.'
                },
                {
                    type: 'short',
                    title: 'Do I get the same parking rates if I don’t book online?',
                    content: 'The quick answer is; probably not! Without a YourParkingSpace booking, you run the risk of paying the drive up rates, not to mention your space is not guaranteed, so you could waste a lot of time and fuel looking for an alternative spot. Most importantly 90% of our spaces can only be booked exclusively via our website.'
                },
                {
                    type: 'long',
                    title: 'Is this space available from the date I need it?',
                    content: 'Parking spaces that appear as a result of your search are all available for instant booking. Spaces which are not available will not show in the results.'
                },
                {
                    type: 'long',
                    title: 'After I book a parking space, is it guaranteed?',
                    content: 'Yes of course! After you book confirmation will be in your account, we’ll also email instructions together with the owner’s contact details so that you can be shown where to park and provided with any key fob, permit or access codes. Also remember you are protected by our Booking Guarantee.'
                },
                {
                    type: 'long',
                    title: 'Can I arrange a viewing of the parking space before booking?',
                    content: 'Unfortunately we are not able to arrange viewings as the full address can only be provided once the booking has been paid for and confirmed. We do however offer our Booking Guarantee which allows customers to \'test\' the parking space, and if you should find it unsuitable for your needs you are entitled to a full refund if you let us know within 48 hours of the rental start date.'
                }
            ];

            resolve({
                data: faqs.filter((faq) => {
                    return faq.type === data.type || !faq.type;
                }),
            });
        });
    }

    /**
     * Search for spaces via custom site ID
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    searchViaId(data) {
        return Vue.http.get(this.route('api.v2.location-search', data));
    }

    /**
     * Generate a pin image for a space
     *
     * @param {Object} data
     *
     * @returns {Promise}
     */
    loadPin(data) {
        return super.get(this.route('ajax.search.pin', data));
    }
}

export default new SpaceRepository();
